import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Image,
  Row,
  Spinner,
} from "react-bootstrap";
import BreadcrumbSection from "components/BreadcrumbSection";
import IMAGES from "constants/images";
import { Icon } from "@iconify/react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import TextInput from "components/TextInput";
import TagSelector from "components/TagSelector";
import PricingFeatureForm from "./PricingFeatureForm";
import VendorSidebar from "inc/VendorSidebar";
import { softwareListing, solutionListing } from "api/CategoryService";
import { fileUpload, getIndustriesList } from "api/authService";
import {
  addProduct,
  fetchProductBySlug,
  integrationsList,
  languagesList,
  marketSegmentsList,
  rolesList,
  updateProduct,
} from "api/ProductService";
import { toast } from "react-toastify";
import { useAuth } from "context/UserContext";
import { Sentry } from "react-activity";
import RHFCKEditor from "components/CKEditor";
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
const MAX_VIDEO_SIZE = 10 * 1024 * 1024; // 10MB in bytes
const MAX_TOTAL_FILES = 5;
const schema = yup.object().shape({
  name: yup
    .string()
    .required("Product Name is required")
    .min(3, "Product Name must be at least 3 characters")
    .max(50, "Product Name cannot exceed 50 characters"),
  websiteUrl: yup
    .string()
    .url("Enter a valid URL")
    .required("Product Website is required"),
  software: yup.array().min(1, "Select at least one software"),
  solution: yup.array().min(1, "Select at least one solution"),
  whoCanUse: yup.array().min(1, "Select at least one role"),
  industries: yup.array().min(1, "Select at least one industry"),
  integrations: yup.array().min(1, "Select at least one integration"),
  languages: yup.array().min(1, "Select at least one language"),
  marketSegment: yup.array().min(1, "Select at least one market segment"),
  brandColors: yup.string().required("Brand Colors is required"),
  description: yup
    .string()
    .min(1, "Description is required")
    .required("Description is required"),
  // .test(
  //   "max-words",
  //   "Description cannot exceed 250 words",
  //   value => {
  //     if (!value) return true;
  //     const wordCount = value.trim().split(/\s+/).filter(Boolean).length;
  //     return wordCount <= 250;
  //   }
  // ),
  features: yup.array().of(
    yup.object().shape({
      title: yup
        .string()
        .max(50, "Title cannot exceed 50 characters")
        .nullable(),
      descriptions: yup
        .array()
        .of(
          yup.object().shape({
            value: yup
              .string()
              .max(50, "Description cannot exceed 50 characters")
              .nullable(),
          })
        )
        .when("title", {
          is: (val) => !!val && val.trim() !== "",
          then: (schema) => schema.required("Descriptions are required"),
          otherwise: (schema) => schema.nullable(),
        }),
    })
  ),

  pricing: yup.array().of(
    yup.object().shape({
      name: yup.string().max(50, "Name cannot exceed 50 characters").nullable(),
      price: yup
        .number()
        .nullable()
        .transform((value) => (isNaN(value) ? null : value))
        .when("name", {
          is: (val) => !!val && val.trim() !== "",
          then: (schema) => schema.required("Price is required"),
        }),
      seats: yup
        .number()
        .nullable()
        .transform((value) => (isNaN(value) ? null : value))
        .when("name", {
          is: (val) => !!val && val.trim() !== "",
          then: (schema) => schema.required("Seats are required"),
        }),
      description: yup
        .string()
        .max(100, "Description cannot exceed 100 characters")
        .nullable()
        .when("name", {
          is: (val) => !!val && val.trim() !== "",
          then: (schema) => schema.required("Description is required "),
        }),
      features: yup
        .array()
        .of(
          yup.object().shape({
            value: yup
              .string()
              .max(50, "Feature cannot exceed 50 characters")
              .nullable(),
          })
        )
        .when("name", {
          is: (val) => !!val && val.trim() !== "",
          then: (schema) => schema.required("Features are required"),
        }),
    })
  ),
});

// const roleOptions = [
//   { value: "project-manager", label: "Project Manager" },
//   { value: "ceo", label: "CEO" },
//   { value: "developer", label: "Developer" },
// ];

// const industriesOptions = [
//   { value: "marketing", label: "Marketing & Advertising" },
//   { value: "itservices", label: "It & Service" },
// ];

// const integrationsOptions = [
//   { value: "slack", label: "Slack" },
//   { value: "zoom", label: "Zoom" },
// ];

// const languagesOptions = [
//   { value: "German", label: "German" },
//   { value: "English", label: "English" },
//   { value: "French", label: "French" },
//   { value: "Italian", label: "Italian" },
// ];
// const segmentOptions = [
//   { value: "Small-Business", label: "Small-Business" },
//   { value: "Mid-Market", label: "Mid-Market" },
// ];

const AddProducts = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const slug = location?.state?.slug;
  const { isLoggedIn, user } = useAuth();
  const userId = user ? user._id : "";
  const [showFollowers, setShowFollowers] = useState(false);
  const [softwareList, setSoftwareList] = useState([]);
  const [solutionList, setSolutionList] = useState([]);
  const [industriesList, setIndustriesList] = useState([]);
  const [roleList, setRoleList] = useState([]);
  const [marketSegmentList, setMarketSegmentList] = useState([]);
  const [integrationList, setIntegrationList] = useState([]);
  const [languageList, setLanguageList] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [images, setImages] = useState([]);
  const [addLoader, setAddLoader] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const imageRef = useRef(null);
  const mediaRef = useRef(null);
  const mapToLabelValue = (data) =>
    data?.map(({ title, _id }) => ({ label: title, value: _id })) || [];

  const fetchData = async () => {
    try {
      const [
        industriesRes,
        softwareRes,
        solutionRes,
        languagesRes,
        rolesRes,
        marketSegmentsRes,
        integrationsRes,
      ] = await Promise.all([
        getIndustriesList(1, 1000),
        softwareListing(1, 1000),
        solutionListing(1, 1000),
        languagesList(1, 1000),
        rolesList(1, 1000),
        marketSegmentsList(1, 1000),
        integrationsList(1, 1000),
      ]);
      console.log("languagesRes", languagesRes);
      setIndustriesList(mapToLabelValue(industriesRes.body.data));
      setSoftwareList(mapToLabelValue(softwareRes.body.data));
      setSolutionList(mapToLabelValue(solutionRes.body.data));
      setLanguageList(mapToLabelValue(languagesRes.body));
      setRoleList(mapToLabelValue(rolesRes.body));
      setMarketSegmentList(mapToLabelValue(marketSegmentsRes.body));
      setIntegrationList(mapToLabelValue(integrationsRes.body));
    } catch (error) {
      console.error("Failed to load lists:", error);
    }
  };

  // Fetch option lists etc.
  useEffect(() => {
    const fetchDataAndSet = async () => {
      setDataLoaded(true);
      await fetchData();
    };
    fetchDataAndSet();
  }, []);
  const {
    control,
    register,
    clearErrors,
    handleSubmit,
    setError,
    getValues,
    reset,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      websiteUrl: "",
      software: [],
      solution: [],
      whoCanUse: [],
      industries: [],
      integrations: [],
      languages: [],
      marketSegment: [],
      brandColors: "#f0f0f0",
      description: "",
      features: [
        {
          title: "",
          descriptions: [{ value: "" }],
        },
      ],
      pricing: [
        {
          name: "",
          price: undefined,
          seats: undefined,
          description: "",
          features: [{ value: "" }],
        },
      ],
    },
  });

  console.log("errors", errors);

  const fetchProduct = async () => {
    try {
      if (!slug) return;
      setPageLoading(true);

      const res = await fetchProductBySlug({ slug, userId });

      if (res.success) {
        const data = res.body;
        setCurrentProduct(data);
        const software = softwareList.filter((v) =>
          data.softwareIds.includes(v.value)
        );
        const solution = solutionList.filter((v) =>
          data.solutionIds.includes(v.value)
        );
        const whoCanUse = mapToLabelValue(data.whoCanUse);
        const industries = mapToLabelValue(data.industries);
        const integrations = mapToLabelValue(data.integrations);
        const languages = mapToLabelValue(data.languages);
        const marketSegment = marketSegmentList.filter((v) =>
          data.marketSegment.includes(v.value)
        );
        const payload = {
          ...data,
          software,
          solution,
          whoCanUse,
          industries,
          integrations,
          languages,
          marketSegment,
          pricing: data?.pricing.map((v) => ({
            ...v,
            price: v?.price?.toString(),
          })),
        };

        reset(payload);
        setImageFile(data.logoUrl);
        setImages(data.mediaUrls);
        setPageLoading(false);
      } else {
        navigate("/");
      }
    } catch (error) {
      console.error("Failed to fetch product details", error);
      setPageLoading(false);
    }
  };

  // Now fetch product only after data is loaded AND slug is present
  useEffect(() => {
    console.log(
      "!dataLoaded && slug && marketSegmentList && softwareList && solutionList",
      !dataLoaded &&
        slug &&
        marketSegmentList.length > 0 &&
        softwareList.length > 0 &&
        solutionList.length > 0
    );
    if (
      !dataLoaded &&
      slug &&
      !dataLoaded &&
      slug &&
      marketSegmentList.length > 0 &&
      softwareList.length > 0 &&
      solutionList.length > 0
    ) {
      fetchProduct();
    } else {
      setDataLoaded(false);
    }
  }, [dataLoaded, slug, reset, marketSegmentList, softwareList, solutionList]);

  const onSubmit = async (data) => {
    try {
      setAddLoader(true);

      // Validate logo
      if (!imageFile) {
        setError("imageFile", {
          type: "manual",
          message: "Logo is required",
        });
        toast.error("Logo is required");
        return;
      }

      // Validate media images
      if (images.length === 0) {
        setError("mediaImages", {
          type: "manual",
          message: "Please upload  product image or video.",
        });
        // toast.error("Please upload product image or video.");
        return;
      }

      if (images.length < 5) {
        setError("mediaImages", {
          type: "manual",
          message: "Please upload at least 5 product images or video.",
        });
        // toast.error("Please upload at least 5 product images or video.");
        return;
      }
      // Upload logo
      let logoUrl = null;
      if (imageFile instanceof File) {
        const res = await fileUpload(imageFile);
        logoUrl = res.body?.[0];
      } else {
        logoUrl = imageFile;
      }
      // Upload media images (using Promise.all for parallel uploads)
      let mediaUrls = [];

      if (images.length > 0) {
        const uploadResults = await Promise.all(
          images.map((img) => {
            if (img instanceof File) {
              return fileUpload(img); // Upload only if it's a File
            } else {
              return Promise.resolve({ body: [img] }); // Already uploaded URL
            }
          })
        );

        mediaUrls = uploadResults.map((r) => r.body?.[0]);
      }

      // Build and send payload
      const payload = {
        ...data,
        logoUrl,
        mediaUrls,
        whoCanUse: data.whoCanUse.map((v) => v.value),
        solutionIds: data.solution.map((v) => v.value),
        softwareIds: data.software.map((v) => v.value),
        marketSegment: data.marketSegment.map((v) => v.value),
        integrations: data.integrations.map((v) => v.value),
        industries: data.industries.map((v) => v.value),
        languages: data.languages.map((v) => v.value),
        features: data.features.length === 1 && data?.features[0]?.title === ""
        ? [] : data.features,
        pricing:
          data.pricing.length === 1 && data?.pricing[0]?.name === ""
            ? []
            : data.pricing.map((v) => ({
                ...v,
                price: parseFloat(v.price),
              })),
      };

      let res = null;
      if (slug) {
        res = await updateProduct({ payload, id: currentProduct._id });
      } else {
        res = await addProduct({ payload });
      }

      if (res.success) {
        toast.success(res.message);
        navigate("/vendor/products");
        // Optionally reset form, image state, etc.
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      console.error("Submit Error:", err);
      toast.error("Something went wrong while adding the product.");
    } finally {
      setAddLoader(false);
    }
  };

  const onError = (errors) => {
    console.log("errors", errors);
    if (!imageFile) {
      setError("imageFile", {
        type: "manual",
        message: "Logo is required",
      });
    }
    if (images.length === 0) {
      setError("mediaImages", {
        type: "manual",
        message: "Please upload  product image or video.",
      });
      // toast.error("Please upload product image or video.");
      return;
    }
  };
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file.");
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Image "${file.name}" exceeds the 5MB size limit.`);
      return;
    }

    setImageFile(file);
    clearErrors("imageFile");
    e.target.value = ""; // reset input for next selection
  };
  const handleMediaChange = (e) => {
    const files = Array.from(e.target.files);

    if (files.length === 0) {
      toast.error("Please select a file.");
      return;
    }

    // Prevent upload if limit already reached
    if (images.length >= MAX_TOTAL_FILES) {
      toast.error("You can upload a maximum of 5 files.");
      return;
    }

    const remainingSlots = MAX_TOTAL_FILES - images.length;

    const validFiles = files
      .slice(0, remainingSlots) // only consider allowed number of new files
      .filter((file) => {
        if (file.type.startsWith("image/")) {
          if (file.size > MAX_IMAGE_SIZE) {
            toast.error(`Image "${file.name}" exceeds the 5MB size limit.`);
            return false;
          }
        } else if (file.type.startsWith("video/")) {
          // Only allow .mp4
          if (!file.name.toLowerCase().endsWith(".mp4")) {
            toast.error(
              `Only .mp4 videos are allowed. "${file.name}" is not supported.`
            );
            return false;
          }
          if (file.size > MAX_VIDEO_SIZE) {
            toast.error(`Video "${file.name}" exceeds the 10MB size limit.`);
            return false;
          }
        } else {
          toast.error(`File "${file.name}" is not a supported media type.`);
          return false;
        }
        return true;
      });

    if (validFiles.length === 0) {
      return;
    }

    // Check if at least one image will be present after adding
    const newImages = [...images, ...validFiles];
    const hasImage = newImages.some(
      (file) => file.type && file.type.startsWith("image/")
    );
    if (!hasImage) {
      toast.error("At least one image is required.");
      return;
    }

    setImages(newImages);
    clearErrors("mediaImages");
    e.target.value = "";
  };
  console.log("errors,getValues()", errors, getValues());

  if (pageLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center "
        style={{ minHeight: "50vh" }}
      >
        <Sentry color="#e91515" size={40} speed={1} animating={true} />
      </div>
    );
  }
  return (
    <Col lg={12} xl={12}>
      <div className="profile_details_style mb-3">
        <h3>{slug ? "Edit" : "Add"} Product</h3>

        <Button
          onClick={() => navigate("/vendor/products")}
          variant="info"
          className="border-radius-100 ms-2 px-4 hAnswers"
        >
          <Icon
            icon="material-symbols:arrow-back-ios-rounded"
            fontSize={16}
            className="me-1"
          />
          Back To All Products
        </Button>
      </div>
      <Form className="mt-3" onSubmit={handleSubmit(onSubmit, onError)}>
        <Row>
          <Col md={6}>
            <TextInput
              labelText={"Product Name"}
              placeholder="Enter Product Name"
              type="text"
              {...register("name")}
              errorMessage={errors.name?.message}
            />
          </Col>
          <Col md={6}>
            <TextInput
              labelText={"Product Website"}
              placeholder="Enter Product Website"
              type="url"
              {...register("websiteUrl")}
              errorMessage={errors.websiteUrl?.message}
            />
          </Col>
          <Col md={6}>
            <Controller
              name="software"
              control={control}
              render={({ field }) => (
                <TagSelector
                  title="Software"
                  placeholder="Select Software"
                  options={softwareList}
                  value={field.value}
                  onChange={field.onChange}
                  {...field}
                  error={errors.software?.message}
                />
              )}
            />
          </Col>
          <Col md={6}>
            <Controller
              name="solution"
              control={control}
              render={({ field }) => (
                <TagSelector
                  title="Solution"
                  placeholder="Select Solution"
                  options={solutionList}
                  value={field.value}
                  onChange={field.onChange}
                  {...field}
                  error={errors.solution?.message}
                />
              )}
            />
          </Col>
          <Col md={6}>
            <Controller
              name="whoCanUse"
              control={control}
              render={({ field }) => (
                <TagSelector
                  title="Who can use"
                  placeholder="Select Who can use"
                  options={roleList}
                  value={field.value}
                  onChange={field.onChange}
                  {...field}
                  error={errors.whoCanUse?.message}
                />
              )}
            />
          </Col>
          <Col md={6}>
            <Controller
              name="industries"
              control={control}
              render={({ field }) => (
                <TagSelector
                  title="Industries"
                  placeholder="Select Industries"
                  options={industriesList}
                  value={field.value}
                  onChange={field.onChange}
                  {...field}
                  error={errors.industries?.message}
                />
              )}
            />
          </Col>

          <Col md={12}>
            <Controller
              name="integrations"
              control={control}
              render={({ field }) => (
                <TagSelector
                  title="Integrations"
                  placeholder="Select Integrations"
                  options={integrationList}
                  value={field.value}
                  onChange={field.onChange}
                  {...field}
                  error={errors.integrations?.message}
                />
              )}
            />
          </Col>
          <Col md={12}>
            <Controller
              name="languages"
              control={control}
              render={({ field }) => (
                <TagSelector
                  title="Languages"
                  placeholder="Select Languages"
                  options={languageList}
                  value={field.value}
                  onChange={field.onChange}
                  {...field}
                  error={errors.languages?.message}
                />
              )}
            />
          </Col>
          <Col md={6}>
            <Controller
              name="marketSegment"
              control={control}
              render={({ field }) => (
                <TagSelector
                  title="Market Segment"
                  placeholder="Select Market Segment"
                  options={marketSegmentList}
                  value={field.value}
                  onChange={field.onChange}
                  {...field}
                  error={errors.marketSegment?.message}
                />
              )}
            />
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Brand Colors</Form.Label>
              <Form.Control
                className="colorType"
                type="color"
                {...register("brandColors")}
              />
            </Form.Group>
          </Col>
          <Col md={12}>
            <Controller
              name="description"
              control={control}
              rules={{ required: "Description is required" }}
              render={({ field, fieldState }) => (
                <RHFCKEditor
                  field={field}
                  fieldState={fieldState}
                  label="Description"
                  placeholder="Enter Description here..."
                  maxHeight={250}
                />
              )}
            />
          </Col>
          <Col md={12}>
            <Form.Group className="mb-3" ref={imageRef}>
              <Form.Label>Upload Product Logo</Form.Label>
              <div className="upload_box_container mt-2">
                <div
                  className="upload_box"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    const droppedFiles = e.dataTransfer.files;
                    if (droppedFiles && droppedFiles.length > 0) {
                      // Pass a fake event with correct structure
                      handleImageChange({ target: { files: droppedFiles } });
                      e.dataTransfer.clearData();
                    }
                  }}
                >
                  <input
                    type="file"
                    id="fileUpload"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => handleImageChange(e)}
                  />
                  <label
                    htmlFor="fileUpload"
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      textAlign: "center",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Icon
                      icon="ep:upload-filled"
                      color="#0071E3"
                      fontSize={50}
                    />
                    <p className="mb-0 upLink">
                      Drag & drop media or{" "}
                      <span
                        style={{
                          textDecoration: "underline",
                          color: "#0071E3",
                        }}
                      >
                        click here
                      </span>
                    </p>
                  </label>
                </div>
                {imageFile && (
                  <div className="upload_box_preview">
                    <Image
                      src={
                        imageFile instanceof File
                          ? URL.createObjectURL(imageFile)
                          : imageFile
                      }
                      alt=""
                    />
                    <Icon
                      onClick={() => setImageFile(null)}
                      icon="zondicons:close-solid"
                      fontSize={20}
                      color="#E91515"
                      className="cursor-pointer"
                    />
                  </div>
                )}
              </div>
              {errors && (
                <div className="text-danger mt-1">
                  {errors?.imageFile?.message}
                </div>
              )}
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Group className="mb-3" ref={mediaRef}>
              <Form.Label>Upload Product Images</Form.Label>
              <div className="upload_box_container upload_box_container1 mt-2">
                <div
                  className="upload_box"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    const files = e.dataTransfer.files;
                    if (files && files.length > 0) {
                      handleMediaChange({ target: { files } });
                      e.dataTransfer.clearData();
                    }
                  }}
                >
                  <input
                    multiple
                    type="file"
                    id="fileUpload1"
                    accept="image/*,video/*"
                    style={{ display: "none" }}
                    onChange={(e) => handleMediaChange(e)}
                  />
                  <label
                    htmlFor="fileUpload1"
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      textAlign: "center",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Icon
                      icon="ep:upload-filled"
                      color="#0071E3"
                      fontSize={50}
                    />
                    <p className="mb-0 upLink">
                      Drag & drop media or{" "}
                      <span
                        style={{
                          textDecoration: "underline",
                          color: "#0071E3",
                        }}
                      >
                        click here
                      </span>
                    </p>
                    <p className="mb-0 text-danger">
                      Max 5 files allowed (10MB each)
                    </p>
                  </label>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  {images?.length > 0 &&
                    images.map((v, index) => {
                      const isFile = v instanceof File;
                      const src = isFile ? URL.createObjectURL(v) : v;

                      // Helper function to check video file type or URL extension
                      const isVideo = () => {
                        if (isFile) {
                          return v.type.startsWith("video/");
                        } else {
                          // Check file extension in URL string (common video extensions)
                          return /\.(mp4|webm|ogg|mov|avi|mkv)$/i.test(src);
                        }
                      };

                      return (
                        <div className="upload_box_preview" key={index}>
                          {isVideo() ? (
                            <video
                              src={src}
                              controls
                              style={{ maxWidth: "100%", maxHeight: "130px" }}
                            />
                          ) : (
                            <Image src={src} alt="" />
                          )}
                          <Icon
                            onClick={() =>
                              setImages((prev) =>
                                prev.filter((_, i) => i !== index)
                              )
                            }
                            icon="zondicons:close-solid"
                            fontSize={20}
                            color="#E91515"
                            className="cursor-pointer"
                          />
                        </div>
                      );
                    })}
                </div>
              </div>
              {errors && (
                <div className="text-danger mt-1">
                  {errors?.mediaImages?.message}
                </div>
              )}
            </Form.Group>
          </Col>

          <PricingFeatureForm
            control={control}
            register={register}
            setValue={setValue}
            errors={errors}
            getValues={getValues}
          />

          <Col md={12} className="text-center mt-2">
            <Button
              disabled={addLoader}
              variant="info"
              className="border-radius-100"
              type="submit"
              style={{
                width: "100%",
                maxWidth: "200px",
                margin: "0 auto",
              }}
            >
              {addLoader ? <Spinner animation="border" size="sm" /> : "Save"}
            </Button>
          </Col>
        </Row>
      </Form>
    </Col>
  );
};

export default AddProducts;
